/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import Select from 'react-select';
import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';
import ProgressBarDialog from '../common/ProgressBar';
import Paginator from '../common/Paginator';
import WebhookFieldMaskPicker from './WebhookFieldMaskPicker';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

export default function EventTriggersTab({ applicationId, navigate }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [triggers, setTriggers] = useState([]);
    const [events, setEvents] = useState([]);
    const [conditionRegistry, setConditionRegistry] = useState(null);
    const [actionRegistry, setActionRegistry] = useState([]);
    const [contextKeys, setContextKeys] = useState(null);

    // Dynamic Data States
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [tables, setTables] = useState([]);
    const [groups, setGroups] = useState([]);
    const [webhooks, setWebhooks] = useState([]);
    const [tableFields, setTableFields] = useState({}); // Cache for table fields { tableId: [...] }

    // Form State
    const [showForm, setShowForm] = useState(false);
    const [editingTrigger, setEditingTrigger] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        event: '',
        enabled: true,
        conditions: [], // [{ field: '', operator: '', value: '' }]
        actions: [] // [{ type: '', config: {} }]
    });
    const [formErrors, setFormErrors] = useState({});

    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    const fetchStaticData = useCallback(async () => {
        try {
            const [evtResp, crResp, actResp, ctxResp, rolesResp, usersResp, tablesResp, groupsResp, webhooksResp] = await Promise.all([
                apiHelper.apiCall('/arcee/events/?no_page=true', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }),
                apiHelper.apiCall('/arcee/events/registry/conditions/', { method: 'GET' }),
                apiHelper.apiCall('/arcee/events/registry/actions/', { method: 'GET' }),
                apiHelper.apiCall('/arcee/events/context-keys/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }),
                apiHelper.apiCall(`/arcee/user/roles/${applicationId}/`, { method: 'GET' }),
                apiHelper.apiCall(`/arcee/user/list/${applicationId}/`, { method: 'GET' }),
                apiHelper.apiCall(`/arcee/application/table/list/?application_id=${applicationId}&page=1&page_size=100`, { method: 'GET' }),
                apiHelper.apiCall(`/arcee/groups/?application_id=${applicationId}`, { method: 'GET' }),
                apiHelper.apiCall(`/arcee/webhooks/?application_id=${applicationId}`, { method: 'GET' })]);

            if (evtResp.ok) {
                const data = await evtResp.json();
                setEvents(Array.isArray(data) ? data : (data.results || []));
            }
            if (crResp.ok) {
                const data = await crResp.json();
                setConditionRegistry(data);
            }
            if (actResp.ok) {
                const data = await actResp.json();
                setActionRegistry(data.actions || []);
            }
            if (ctxResp.ok) {
                const data = await ctxResp.json();
                setContextKeys(data);
            }
            if (rolesResp && rolesResp.ok) {
                const data = await rolesResp.json();
                setRoles(Array.isArray(data) ? data : (data.results || []));
            }
            if (usersResp && usersResp.ok) {
                const data = await usersResp.json();
                setUsers(Array.isArray(data) ? data : (data.results || []));
            }
            if (tablesResp && tablesResp.ok) {
                const data = await tablesResp.json();
                setTables(Array.isArray(data) ? data : (data.results || []));
            }
            if (groupsResp && groupsResp.ok) {
                const data = await groupsResp.json();
                setGroups(Array.isArray(data) ? data : (data.results || []));
            }
            if (webhooksResp && webhooksResp.ok) {
                const data = await webhooksResp.json();
                setWebhooks(Array.isArray(data) ? data : (data.results || []));
            }
        } catch (error) {
            console.error("Error fetching static data", error);
        }
    }, [applicationId]);

    const fetchTriggers = useCallback(async () => {
        setLoading(true);
        try {
            const trResp = await apiHelper.apiCall(`/arcee/event-triggers/?page=${currentPage}&page_size=${itemsPerPage}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
            });

            if (trResp.ok) {
                const data = await trResp.json();
                setTriggers(data.results || []);
                setTotalRecords(data.count || 0);
            }
        } catch (error) {
            console.error("Error fetching event triggers tab data", error);
            showAlertDialog({ title: 'Error', message: 'Failed to load trigger data.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applicationId, currentPage, itemsPerPage, showAlertDialog]);

    const fetchTableFields = async (tableId) => {
        if (!tableId || tableFields[tableId]) return; // Already cached
        try {
            const resp = await apiHelper.apiCall(`/arcee/application/table/details/?table_id=${tableId}`, { method: 'GET' });
            if (resp.ok) {
                const data = await resp.json();
                setTableFields(prev => ({ ...prev, [tableId]: data.fields || [] }));
            }
        } catch (error) {
            console.error("Failed to fetch table fields", error);
        }
    };

    useEffect(() => {
        fetchStaticData();
    }, [fetchStaticData]);

    useEffect(() => {
        fetchTriggers();
    }, [fetchTriggers]);

    const handleFormOpen = (tr = null) => {
        if (tr) {
            setEditingTrigger(tr);
            setFormData({
                name: tr.name || '',
                event: tr.event || '', // Need the actual event ID if possible
                enabled: tr.enabled !== false,
                conditions: (tr.conditions || []).map(c => ({ ...c })),
                actions: (tr.actions || []).map(a => ({ type: a.type, config: { ...(a.config || {}) } }))
            });
        } else {
            setEditingTrigger(null);
            setFormData({
                name: '',
                event: '',
                enabled: true,
                conditions: [],
                actions: []
            });
        }
        setFormErrors({});
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingTrigger(null);
    };

    // Form interactions
    const addCondition = () => {
        setFormData(prev => ({
            ...prev,
            conditions: [...prev.conditions, { field: '', operator: 'eq', value: '' }]
        }));
    };

    const removeCondition = (index) => {
        setFormData(prev => {
            const nc = [...prev.conditions];
            nc.splice(index, 1);
            return { ...prev, conditions: nc };
        });
    };

    const updateCondition = (index, key, val) => {
        setFormData(prev => {
            const nc = [...prev.conditions];
            nc[index] = { ...nc[index], [key]: val };
            return { ...prev, conditions: nc };
        });
    };

    const addAction = () => {
        setFormData(prev => ({
            ...prev,
            actions: [...prev.actions, { type: '', config: {} }]
        }));
    };

    const removeAction = (index) => {
        setFormData(prev => {
            const na = [...prev.actions];
            na.splice(index, 1);
            return { ...prev, actions: na };
        });
    };

    const updateActionType = (index, type) => {
        setFormData(prev => {
            const na = [...prev.actions];
            na[index] = { type, config: {} };
            return { ...prev, actions: na };
        });
    };

    const updateActionConfig = (index, key, val) => {
        setFormData(prev => {
            const na = [...prev.actions];
            na[index] = { ...na[index], config: { ...na[index].config, [key]: val } };
            return { ...prev, actions: na };
        });
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.name.trim()) errs.name = 'Trigger name is required.';
        if (!formData.event) errs.event = 'Base Event is required.';

        let hasConditionError = false;
        formData.conditions.forEach((c, i) => {
            if (!c.field || !c.operator) hasConditionError = true;
            if (c.value === '') hasConditionError = true; // Value shouldn't be empty typically
        });
        if (hasConditionError) errs.conditions = 'All condition fields are required.';

        let hasActionError = false;
        if (formData.actions.length === 0) errs.actions = 'At least one action is required.';
        formData.actions.forEach((a, i) => {
            if (!a.type) { hasActionError = true; return; }

            if (a.type === 'call_webhook') {
                if (!a.config.webhook_endpoint_id) {
                    hasActionError = true;
                    errs.actions = 'Please select a Webhook Endpoint for the Call Webhook action.';
                    return;
                }
                const selEvt = events.find(e => e.id.toString() === (formData.event || '').toString());
                if (selEvt && selEvt.event_type === 'record') {
                    if (!a.config.field_mask_table_id) {
                        hasActionError = true;
                        errs.actions = 'Webhook action requires a table selection for field mask.';
                        return;
                    }
                    if (!a.config.field_mask || a.config.field_mask.length === 0) {
                        hasActionError = true;
                        errs.actions = 'Select at least one field to include in the webhook payload.';
                        return;
                    }
                }
            }

            if (a.type === 'send_notification') {
                if (!a.config.recipient_type) {
                    hasActionError = true;
                    errs.actions = 'Please select a Recipient Strategy for the Send Notification action.';
                    return;
                }
                const needsRecipient = !['all_users', 'contextual'].includes(a.config.recipient_type);
                if (needsRecipient && !a.config.recipient_id) {
                    hasActionError = true;
                    errs.actions = 'Please select a Recipient for the Send Notification action.';
                    return;
                }
            }
        });
        if (hasActionError && !errs.actions) errs.actions = 'All actions must have a valid type.';

        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const doSave = async () => {
        const payload = {
            application_id: parseInt(applicationId, 10),
            name: formData.name,
            event: parseInt(formData.event, 10),
            enabled: formData.enabled,
            conditions: formData.conditions,
            actions: formData.actions
        };

        try {
            let resp;
            if (editingTrigger) {
                resp = await apiHelper.apiCall(`/arcee/event-triggers/${editingTrigger.id}/`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                resp = await apiHelper.apiCall('/arcee/event-triggers/create/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                const errStr = typeof err === 'object' && !err.error
                    ? Object.values(err).map(v => Array.isArray(v) ? v.join(', ') : String(v)).join(' ')
                    : (err.error || 'Failed to save trigger');
                throw new Error(errStr);
            }

            handleFormClose();
            fetchTriggers();
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleSave = () => {
        if (!validateForm()) return;
        doSave();
    };

    const handleDelete = (tr) => {
        showAlertDialog({
            title: 'Delete Trigger',
            message: `Are you sure you want to delete "${tr.name}"? This cannot be undone.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/event-triggers/${tr.id}/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error('Failed to delete');
                    fetchTriggers();
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const handleToggleActive = async (tr) => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/event-triggers/${tr.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: !tr.enabled })
            });

            if (!resp.ok) throw new Error('Failed to toggle active state');
            fetchTriggers();
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    // Helper for rendering available context keys in condition field dropdown
    const renderContextKeyOptions = () => {
        if (!contextKeys || !formData.event) return null;
        const selectedEvent = events.find(e => e.id.toString() === formData.event.toString());
        if (!selectedEvent || selectedEvent.event_type !== 'record') return null;

        const options = [];

        // Tables keys
        if (contextKeys.tables) {
            Object.keys(contextKeys.tables).forEach(tableName => {
                const keys = contextKeys.tables[tableName];
                if (!keys || keys.length === 0) return;

                let tableAllowed = false;
                if (selectedEvent.table_scope === 'all') {
                    tableAllowed = true;
                } else if (selectedEvent.scoped_tables?.includes(keys[0].table_id)) {
                    tableAllowed = true;
                }

                if (tableAllowed) {
                    options.push(<optgroup key={`tb-${tableName}`} label={`Table: ${tableName}`}>
                        {keys.map(k => <option key={`tbk-${k.key}`} value={k.key}>{k.display_name} ({k.key})</option>)}
                    </optgroup>);
                }
            });
        }
        return options;
    };

    const pageCount = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) setCurrentPage(newPage);
    };
    const handlePrevious = () => setCurrentPage(p => Math.max(p - 1, 1));
    const handleNext = () => setCurrentPage(p => Math.min(p + 1, pageCount));

    return (
        <div className="w-100">
            {loading ? <ProgressBarDialog open={true} text_message="Loading triggers..." /> : null}

            <div className="d-flex align-items-center justify-content-between mb-4 mt-2">
                <div>
                    <h5 className="fw-bold mb-1">Configured Event Triggers</h5>
                    <p className="text-muted small mb-0">Automate actions based on specific conditions when an event occurs.</p>
                </div>
                <button
                    className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                    onClick={() => handleFormOpen()}
                    style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius || '6px' }}
                >
                    <i className="bi bi-plus-lg"></i>
                    Add Trigger
                </button>
            </div>

            {/* Warn if no events exist */}
            {events.length === 0 && !loading ? (
                <div className="alert alert-warning py-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    You must <strong>configure at least one Event</strong> in the Events tab before you can create triggers.
                </div>
            ) : null}

            {/* List Triggers */}
            {triggers.length === 0 && events.length > 0 && !loading && (
                <div className="text-center text-muted py-5 bg-white rounded shadow-sm border border-dashed">
                    <i className="bi bi-diagram-3 fs-1 mb-3 d-block opacity-50"></i>
                    No triggers configured yet.<br />
                    <small>Click "Add Trigger" to automate actions.</small>
                </div>
            )}

            <div className="row g-3">
                {triggers.map(tr => {
                    const parentEvent = events.find(e => e.id === tr.event) || (tr.event && typeof tr.event === 'object' ? tr.event : null);
                    return (
                        <div className="col-12 col-md-6 col-lg-4" key={tr.id}>
                            <div className={`card h-100 border-0 shadow-sm ${!tr.enabled ? 'opacity-75' : ''}`} style={{ borderRadius: theme.cardRadius || '8px', minHeight: '220px' }}>
                                <div className="card-body p-4 position-relative d-flex flex-column">
                                    <div className="position-absolute top-0 end-0 p-3 pt-3">
                                        <div className="dropdown">
                                            <button className="btn btn-sm btn-light border-0" type="button" data-bs-toggle="dropdown">
                                                <i className="bi bi-three-dots-vertical"></i>
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end shadow border-0" style={{ borderRadius: '8px', fontSize: '14px' }}>
                                                <li><button className="dropdown-item" onClick={() => handleFormOpen(tr)}><i className="bi bi-pencil me-2 text-primary"></i> Edit</button></li>
                                                <li>
                                                    <button className="dropdown-item" onClick={() => handleToggleActive(tr)}>
                                                        {tr.enabled ? <><i className="bi bi-pause-circle me-2 text-warning"></i> Disable</> : <><i className="bi bi-play-circle me-2 text-success"></i> Enable</>}
                                                    </button>
                                                </li>
                                                <li><hr className="dropdown-divider" /></li>
                                                <li><button className="dropdown-item text-danger" onClick={() => handleDelete(tr)}><i className="bi bi-trash me-2"></i> Delete</button></li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="mb-auto">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span className={`badge ${tr.enabled ? 'bg-success bg-opacity-10 text-success' : 'bg-secondary bg-opacity-10 text-secondary'} border px-2 py-1`} style={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                                {tr.enabled ? 'Active' : 'Disabled'}
                                            </span>
                                        </div>
                                        <h6 className="fw-bold fs-5 text-dark mb-1">{tr.name}</h6>

                                        <div className="text-muted small mb-3 p-2 bg-light rounded d-inline-block">
                                            <i className="bi bi-lightning-charge me-1"></i>
                                            {parentEvent ? (parentEvent.display_name || parentEvent.event_key) : `Event ID: ${tr.event}`}
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top">
                                        <div className="row text-center g-2 text-muted small">
                                            <div className="col-6 border-end">
                                                <div className="fw-bold fs-5 text-dark">{(tr.conditions || []).length}</div>
                                                Conditions
                                            </div>
                                            <div className="col-6">
                                                <div className="fw-bold fs-5 text-dark">{(tr.actions || []).length}</div>
                                                Actions
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {totalRecords > itemsPerPage && (
                <Paginator
                    page={currentPage}
                    pageCount={pageCount}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onPage={handlePageChange}
                    maxPagesToShow={3}
                />
            )}

            {/* Trigger Wizard Modal */}
            {showForm && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow" style={{ borderRadius: theme.cardRadius || '12px' }}>
                            <div className="modal-header border-bottom-0 pb-0 pt-4 px-4 bg-white d-flex flex-column align-items-start gap-2">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <h5 className="modal-title fw-bold text-dark">{editingTrigger ? 'Edit Trigger' : 'New Trigger Workflow'}</h5>
                                    <button type="button" className="btn-close" onClick={handleFormClose}></button>
                                </div>
                            </div>

                            <div className="modal-body p-0 bg-light">
                                <div className="row g-0 h-100">
                                    {/* General Info Column */}
                                    <div className="col-12 col-md-4 p-4 bg-white border-end shadow-sm">
                                        <h6 className="fw-bold small text-uppercase text-muted mb-4 border-bottom pb-2">1. Strategy</h6>

                                        <div className="mb-4">
                                            <label className="fw-bold small text-muted mb-1">Trigger Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g. Notify Admins on High Value"
                                                style={{ borderRadius: theme.inputRadius || '6px' }}
                                            />
                                            {formErrors.name && <div className="text-danger small mt-1"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.name}</div>}
                                        </div>

                                        <div className="mb-4">
                                            <label className="fw-bold small text-muted mb-1">Base Event <span className="text-danger">*</span></label>
                                            <select
                                                className={`form-select ${formErrors.event ? 'is-invalid' : ''}`}
                                                value={formData.event || (editingTrigger && typeof editingTrigger.event === 'object' ? editingTrigger.event.id : editingTrigger?.event || '')}
                                                onChange={e => setFormData({ ...formData, event: e.target.value })}
                                                style={{ borderRadius: theme.inputRadius || '6px' }}
                                                disabled={!!editingTrigger} // Disallow changing event on edit to prevent condition breaks
                                            >
                                                <option value="">Select Configured Event...</option>
                                                {events.map(evt => (
                                                    <option key={evt.id} value={evt.id}>{evt.display_name} ({evt.event_key})</option>
                                                ))}
                                            </select>
                                            {formErrors.event && <div className="text-danger small mt-1"><i className="bi bi-exclamation-circle me-1"></i>{formErrors.event}</div>}
                                        </div>

                                        <div className="form-check form-switch mt-4 pt-3 border-top">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="activeSwitch"
                                                checked={formData.enabled}
                                                onChange={e => setFormData({ ...formData, enabled: e.target.checked })}
                                            />
                                            <label className="form-check-label small fw-bold text-muted ms-2 mt-1" htmlFor="activeSwitch">Active</label>
                                        </div>
                                    </div>

                                    {/* Conditions & Actions Column */}
                                    <div className="col-12 col-md-8 p-4 bg-light overflow-auto">
                                        <h6 className="fw-bold small text-uppercase text-muted mb-4 border-bottom pb-2">2. Conditions & Actions</h6>

                                        {formErrors.conditions && <div className="alert alert-danger py-2 small mb-3"><i className="bi bi-exclamation-circle me-2"></i>{formErrors.conditions}</div>}
                                        {formErrors.actions && <div className="alert alert-danger py-2 small mb-3"><i className="bi bi-exclamation-circle me-2"></i>{formErrors.actions}</div>}

                                        {/* Conditions List */}
                                        {(() => {
                                            const selEvent = events.find(e => e.id.toString() === (formData.event || '').toString());
                                            if (!selEvent) return null;
                                            if (selEvent.event_type !== 'record') {
                                                return (
                                                    <div className="mb-4 bg-white p-3 rounded shadow-sm border text-center text-muted small">
                                                        <i className="bi bi-info-circle me-2 fs-5 d-block mb-1"></i>
                                                        Conditions are only available for Record events.<br />This trigger will execute unconditionally when the event fires.
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="mb-5 bg-white p-3 rounded shadow-sm border">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 20, height: 20, fontSize: 11 }}>IF</div>
                                                            Conditions <span className="badge bg-light text-dark border fw-normal">{formData.conditions.length}</span>
                                                        </div>
                                                        <button className="btn btn-sm btn-outline-primary" onClick={addCondition}>
                                                            <i className="bi bi-plus"></i> Add Condition
                                                        </button>
                                                    </div>

                                                    {formData.conditions.length === 0 ? (
                                                        <div className="text-center text-muted py-3 small bg-light rounded">No conditions. Trigger will always execute on event.</div>
                                                    ) : (
                                                        <div className="d-flex flex-column gap-2 mb-2">
                                                            {formData.conditions.map((c, idx) => (
                                                                <div key={idx} className="d-flex align-items-start gap-2 p-2 bg-light border rounded position-relative pe-5">
                                                                    <div className="row g-2 w-100">
                                                                        <div className="col-12 col-sm-4">
                                                                            <div className="input-group input-group-sm">
                                                                                <span className="input-group-text bg-white small text-muted">Field</span>
                                                                                <input
                                                                                    type="text"
                                                                                    list="contextKeyOptions"
                                                                                    className="form-control"
                                                                                    value={c.field}
                                                                                    onChange={e => updateCondition(idx, 'field', e.target.value)}
                                                                                    placeholder="record.amount"
                                                                                />
                                                                                <datalist id="contextKeyOptions">
                                                                                    {renderContextKeyOptions()}
                                                                                </datalist>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-12 col-sm-4">
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={c.operator}
                                                                                onChange={e => updateCondition(idx, 'operator', e.target.value)}
                                                                            >
                                                                                {conditionRegistry?.operators?.map(op => (
                                                                                    <option key={op.key} value={op.key}>{op.display_name}</option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                        <div className="col-12 col-sm-4">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={c.value}
                                                                                onChange={e => updateCondition(idx, 'value', e.target.value)}
                                                                                placeholder="Value"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        className="btn btn-sm btn-link text-danger position-absolute top-50 translate-middle-y end-0 me-2"
                                                                        onClick={() => removeCondition(idx)}
                                                                    >
                                                                        <i className="bi bi-x-circle"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {/* Actions List */}
                                        <div className="bg-white p-3 rounded shadow-sm border">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                    <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 20, height: 20, fontSize: 11 }}>DO</div>
                                                    Actions <span className="badge bg-light text-dark border fw-normal">{formData.actions.length}</span>
                                                </div>
                                                <button className="btn btn-sm btn-outline-success" onClick={addAction}>
                                                    <i className="bi bi-plus"></i> Add Action
                                                </button>
                                            </div>

                                            {formData.actions.length === 0 ? (
                                                <div className="text-center text-danger py-3 small bg-light rounded"><i className="bi bi-exclamation-triangle me-1"></i> You must add at least one action.</div>
                                            ) : (
                                                <div className="d-flex flex-column gap-3 mb-2">
                                                    {formData.actions.map((act, idx) => {
                                                        const registryAct = actionRegistry.find(r => r.key === act.type);
                                                        return (
                                                            <div key={idx} className="p-3 bg-light border rounded position-relative">
                                                                <button
                                                                    className="btn btn-sm btn-light text-danger position-absolute top-0 end-0 m-2 border"
                                                                    onClick={() => removeAction(idx)}
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                                <div className="mb-3 pe-5">
                                                                    <label className="small fw-bold text-muted mb-1">Action Type</label>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={act.type}
                                                                        onChange={e => updateActionType(idx, e.target.value)}
                                                                    >
                                                                        <option value="">Select Action...</option>
                                                                        {actionRegistry.map(r => (
                                                                            <option key={r.key} value={r.key}>{r.display_name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                                {act.type && registryAct && (
                                                                    <div className="p-3 bg-white border rounded shadow-sm">
                                                                        <h6 className="fw-bold small text-primary mb-3"><i className="bi bi-gear me-2"></i> Configuration Data</h6>
                                                                        <div className="row g-3">
                                                                            {registryAct.config_schema?.fields?.filter(schema => {
                                                                                if (schema.depends_on) {
                                                                                    const depValue = act.config[schema.depends_on];
                                                                                    if (!depValue) return false;

                                                                                    if (schema.visible_when) {
                                                                                        const rules = Array.isArray(schema.visible_when) ? schema.visible_when : [schema.visible_when];
                                                                                        if (!rules.includes(depValue)) return false;
                                                                                    }
                                                                                    if (schema.hidden_when) {
                                                                                        const rules = Array.isArray(schema.hidden_when) ? schema.hidden_when : [schema.hidden_when];
                                                                                        if (rules.includes(depValue)) return false;
                                                                                    }
                                                                                }
                                                                                return true;
                                                                            }).map(schema => {
                                                                                // REPEATABLE ROW RENDERER
                                                                                if (schema.type === 'repeatable_row') {
                                                                                    const rows = act.config[schema.key] || [];

                                                                                    const addRow = () => {
                                                                                        const newRow = {};
                                                                                        (schema.row_schema || []).forEach(rs => newRow[rs.key] = '');
                                                                                        updateActionConfig(idx, schema.key, [...rows, newRow]);
                                                                                    };

                                                                                    const updateRow = (rIdx, rKey, rVal) => {
                                                                                        const newRows = [...rows];
                                                                                        newRows[rIdx] = { ...newRows[rIdx], [rKey]: rVal };
                                                                                        updateActionConfig(idx, schema.key, newRows);
                                                                                    };

                                                                                    const removeRow = (rIdx) => {
                                                                                        const newRows = [...rows];
                                                                                        newRows.splice(rIdx, 1);
                                                                                        updateActionConfig(idx, schema.key, newRows);
                                                                                    };

                                                                                    // Fetch fields if target table is selected
                                                                                    const targetTableId = act.config['target_table_id'];
                                                                                    if (targetTableId && !tableFields[targetTableId]) {
                                                                                        fetchTableFields(targetTableId);
                                                                                    }

                                                                                    return (
                                                                                        <div className="col-12 mt-3" key={schema.key}>
                                                                                            <label className="small fw-bold text-muted mb-2">{schema.display_name || schema.key}</label>
                                                                                            <div className="p-2 border rounded bg-white">
                                                                                                {rows.length === 0 && <div className="text-muted small text-center p-2 fst-italic">No mappings added.</div>}
                                                                                                {rows.map((row, rIdx) => (
                                                                                                    <div key={rIdx} className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom position-relative">
                                                                                                        <div className="row g-2 flex-grow-1">
                                                                                                            {(schema.row_schema || []).map(rs => (
                                                                                                                <div className="col" key={rs.key}>
                                                                                                                    <span className="small text-muted d-block mb-1" style={{ fontSize: '10px' }}>{rs.display_name}</span>
                                                                                                                    {rs.type === 'dynamic_select' && rs.data_source === 'target_table_fields' ? (
                                                                                                                        <Select
                                                                                                                            options={(tableFields[targetTableId] || []).map(f => ({ value: f.id, label: f.name }))}
                                                                                                                            value={(tableFields[targetTableId] || []).map(f => ({ value: f.id, label: f.name })).find(o => o.value == row[rs.key]) || null}
                                                                                                                            onChange={opt => updateRow(rIdx, rs.key, opt ? opt.value : '')}
                                                                                                                            classNamePrefix="react-select"
                                                                                                                            menuPortalTarget={document.body}
                                                                                                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                                                                        />
                                                                                                                    ) : rs.type === 'select' ? (
                                                                                                                        <select className="form-select form-select-sm" value={row[rs.key] || ''} onChange={e => updateRow(rIdx, rs.key, e.target.value)}>
                                                                                                                            <option value="">Select...</option>
                                                                                                                            {rs.options_from === 'condition_registry.value_sources' ? (
                                                                                                                                conditionRegistry?.value_sources?.map(vs => <option key={vs.key} value={vs.key}>{vs.display_name}</option>)
                                                                                                                            ) : null}
                                                                                                                        </select>
                                                                                                                    ) : (
                                                                                                                        <input type="text" className="form-control form-control-sm" value={row[rs.key] || ''} onChange={e => updateRow(rIdx, rs.key, e.target.value)} />
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                        <button className="btn btn-sm btn-link text-danger" onClick={() => removeRow(rIdx)}><i className="bi bi-x-circle"></i></button>
                                                                                                    </div>
                                                                                                ))}
                                                                                                <button className="btn btn-sm btn-outline-primary mt-1" onClick={addRow}><i className="bi bi-plus"></i> Add Mapping</button>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                } // End repeatable_row

                                                                                // DYNAMIC SELECT RENDERER
                                                                                if (schema.type === 'dynamic_select') {
                                                                                    let options = [];

                                                                                    if (schema.data_source === '/arcee/application/tables/') {
                                                                                        options = tables.map(t => ({ value: t.id, label: t.name }));
                                                                                    } else if (schema.data_source === '/arcee/webhooks/') {
                                                                                        options = webhooks.filter(w => w.is_active).map(w => ({ value: w.id, label: w.name }));
                                                                                    } else if (typeof schema.data_source === 'object') {
                                                                                        const depVal = act.config[schema.depends_on];
                                                                                        const entityUrl = schema.data_source[depVal];
                                                                                        if (entityUrl?.includes('roles')) options = roles.map(r => ({ value: r.id, label: r.name }));
                                                                                        else if (entityUrl?.includes('users')) options = users.map(u => ({ value: u.id, label: u.username || u.email }));
                                                                                        else if (entityUrl?.includes('groups')) options = groups.map(g => ({ value: g.id, label: g.name }));
                                                                                    }

                                                                                    return (
                                                                                        <div className="col-12" key={schema.key}>
                                                                                            <label className="small fw-bold text-muted mb-1">{schema.display_name || schema.key}</label>
                                                                                            <Select
                                                                                                options={options}
                                                                                                value={options.find(o => o.value == act.config[schema.key]) || null}
                                                                                                onChange={opt => {
                                                                                                    updateActionConfig(idx, schema.key, opt ? opt.value : '');
                                                                                                    // Trigger table field fetch if this is a target table
                                                                                                    if (schema.key === 'target_table_id' && opt) fetchTableFields(opt.value);
                                                                                                }}
                                                                                                isClearable
                                                                                                placeholder={`Search ${schema.display_name}...`}
                                                                                                classNamePrefix="react-select"
                                                                                                menuPortalTarget={document.body}
                                                                                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                                            />
                                                                                        </div>
                                                                                    );
                                                                                } // End dynamic_select

                                                                                return (
                                                                                    <div className="col-12" key={schema.key}>
                                                                                        <label className="small fw-bold text-muted mb-1">{schema.display_name || schema.key}</label>
                                                                                        {schema.type === 'select' ? (
                                                                                            <select className="form-select form-select-sm" value={act.config[schema.key] || ''} onChange={e => updateActionConfig(idx, schema.key, e.target.value)}>
                                                                                                <option value="">Select...</option>
                                                                                                {schema.options?.map(opt => <option key={opt.key} value={opt.key}>{opt.display_name}</option>)}
                                                                                            </select>
                                                                                        ) : schema.type === 'boolean' ? (
                                                                                            <div className="form-check">
                                                                                                <input type="checkbox" className="form-check-input" checked={!!act.config[schema.key]} onChange={e => updateActionConfig(idx, schema.key, e.target.checked)} />
                                                                                            </div>
                                                                                        ) : (
                                                                                            <input type={schema.type === 'integer' ? 'number' : 'text'} className="form-control form-control-sm" value={act.config[schema.key] || ''} onChange={e => updateActionConfig(idx, schema.key, schema.type === 'integer' ? parseInt(e.target.value, 10) : e.target.value)} placeholder={`Enter ${schema.display_name || schema.key}`} />
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        {act.type === 'call_webhook' && (() => {
                                                                            const selEvt = events.find(
                                                                                e => e.id.toString() === (formData.event || '').toString()
                                                                            );
                                                                            return (
                                                                                <WebhookFieldMaskPicker
                                                                                    selectedEvent={selEvt}
                                                                                    allTables={tables}
                                                                                    actionConfig={act.config}
                                                                                    onConfigChange={(key, val) => updateActionConfig(idx, key, val)}
                                                                                    theme={theme}
                                                                                    applicationId={applicationId}
                                                                                />
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-top px-4 py-3 bg-white d-flex justify-content-end gap-2" style={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                                <button type="button" className="btn btn-light" onClick={handleFormClose} style={{ borderRadius: theme.buttonRadius || '6px' }}>Cancel</button>
                                <button type="button" className="btn btn-primary shadow-sm" onClick={handleSave} style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius || '6px' }}>
                                    {editingTrigger ? 'Save Trigger' : 'Create Trigger'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
